import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { FlowExecutionStatus } from '@shared/types/flow-executions';
import type {
  ExecutionChatBlockedStep,
  ExecutionChatComposerState,
  ExecutionChatHitlRequestCas,
  ExecutionChatMessageRow,
  ExecutionChatMode,
  ExecutionChatResponse,
} from '@shared/types/execution-chat';
import { sortExecutionChatMessages } from '@/components/execution-chat/sortMessages';
import {
  ExecutionChatStreamError,
  invokeExecutionChat,
  streamExecutionChat,
} from '@/lib/execution-chat';
import { attachExecutionChatHitlCas } from '@/lib/execution-chat-cas';

const mergeMessages = (
  previous: ExecutionChatMessageRow[],
  incoming: ExecutionChatMessageRow[]
): ExecutionChatMessageRow[] => {
  const map = new Map<string, ExecutionChatMessageRow>();
  for (const message of previous) map.set(message.id, message);
  for (const message of incoming) map.set(message.id, message);
  return sortExecutionChatMessages([...map.values()]);
};

const getInitialMode = (status: FlowExecutionStatus | null): ExecutionChatMode => {
  if (status === 'completed' || status === 'cancelled') return 'archived';
  if (status === 'failed' || status === 'paused') return 'active';
  return 'read_only';
};

const getInitialComposerState = (
  status: FlowExecutionStatus | null
): ExecutionChatComposerState => {
  if (status === 'completed') {
    return {
      enabled: false,
      reason: '',
    };
  }
  if (status === 'cancelled') {
    return {
      enabled: false,
      reason: 'Exécution annulée. Historique uniquement.',
    };
  }
  if (status === 'failed') {
    return {
      enabled: true,
      reason:
        'Exécution en échec: vous pouvez demander un diagnostic ou relancer la dernière action.',
    };
  }
  if (status === 'running' || status === 'pending') {
    return {
      enabled: true,
      reason:
        'Exécution en cours: les actions seront possibles quand le travail sera en pause.',
    };
  }
  return {
    enabled: true,
    reason: 'Chat actif.',
  };
};

const REALTIME_MESSAGE_CHUNK_MAX_CHARS = 72;
const REALTIME_MESSAGE_CHUNK_DELAY_MS = 18;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const chunkText = (text: string): string[] => {
  const tokens = text.match(/\S+\s*/g) ?? [];
  if (tokens.length === 0) return text.length > 0 ? [text] : [];

  const chunks: string[] = [];
  let current = '';

  for (const token of tokens) {
    if (
      current.length > 0 &&
      current.length + token.length > REALTIME_MESSAGE_CHUNK_MAX_CHARS
    ) {
      chunks.push(current);
      current = '';
    }
    current += token;
  }

  if (current.length > 0) chunks.push(current);
  return chunks;
};

const getStreamablePersistedMessageText = (
  message: ExecutionChatMessageRow
): string | null => {
  if (message.actor !== 'assistant' || message.kind !== 'assistant_message') {
    return null;
  }

  const payload = (message as ExecutionChatMessageRow<'assistant_message'>).payload;
  if (payload.metadata?.resultCard) return null;

  const text = payload.text.trim();
  return text.length > 0 ? payload.text : null;
};

const shouldStreamPersistedMessage = (
  message: ExecutionChatMessageRow,
  executionStatus: FlowExecutionStatus | null,
  isSubmitting: boolean
): boolean => {
  if (isSubmitting) return false;
  if (executionStatus !== 'pending' && executionStatus !== 'running') return false;
  return getStreamablePersistedMessageText(message) !== null;
};

export interface UseExecutionChatStateResult {
  messages: ExecutionChatMessageRow[];
  mode: ExecutionChatMode;
  blockedStep: ExecutionChatBlockedStep | null;
  availableActions: string[];
  composerState: ExecutionChatComposerState;
  hitlRequestCas: ExecutionChatHitlRequestCas | null;
  isLoading: boolean;
  isSubmitting: boolean;
  /** Live streamed assistant text while a conversational reply is being generated. */
  streamingText: string | null;
  /** Short transient status while the server prepares a streamed reply. */
  streamingStatus: string | null;
  error: string | null;
  refresh: (options?: { silent?: boolean }) => Promise<void>;
  runAct: (
    actionId: string,
    args?: Record<string, unknown>,
    options?: { clientMessage?: string }
  ) => Promise<boolean>;
  sendMessage: (message: string) => Promise<boolean>;
}

export const useExecutionChatState = (
  executionId: string | null,
  executionStatus: FlowExecutionStatus | null
): UseExecutionChatStateResult => {
  const [messages, setMessages] = useState<ExecutionChatMessageRow[]>([]);
  const [mode, setMode] = useState<ExecutionChatMode>(() =>
    getInitialMode(executionStatus)
  );
  const [composerState, setComposerState] = useState<ExecutionChatComposerState>(() =>
    getInitialComposerState(executionStatus)
  );
  const [blockedStep, setBlockedStep] = useState<ExecutionChatBlockedStep | null>(null);
  const [availableActions, setAvailableActions] = useState<string[]>([]);
  const [hitlRequestCas, setHitlRequestCas] =
    useState<ExecutionChatHitlRequestCas | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(executionId));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastKnownCreatedAtRef = useRef<string | null>(null);
  const activeExecutionIdRef = useRef<string | null>(executionId);
  const executionStatusRef = useRef<FlowExecutionStatus | null>(executionStatus);
  const isSubmittingRef = useRef(isSubmitting);
  const persistedMessageStreamQueueRef = useRef<Promise<void>>(Promise.resolve());
  const streamingPersistedMessageIdsRef = useRef(new Set<string>());

  useEffect(() => {
    activeExecutionIdRef.current = executionId;
  }, [executionId]);

  useEffect(() => {
    executionStatusRef.current = executionStatus;
  }, [executionStatus]);

  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  useEffect(() => {
    setMode(getInitialMode(executionStatus));
    setComposerState(getInitialComposerState(executionStatus));
  }, [executionStatus]);

  useEffect(() => {
    setMessages([]);
    setBlockedStep(null);
    setAvailableActions([]);
    setHitlRequestCas(null);
    setError(null);
    setStreamingText(null);
    setStreamingStatus(null);
    setIsLoading(Boolean(executionId));
  }, [executionId]);

  const applyPayload = useCallback((payload: ExecutionChatResponse) => {
    const visibleMessages = payload.messages.filter(
      (message) => !streamingPersistedMessageIdsRef.current.has(message.id)
    );
    setMessages((previous) => mergeMessages(previous, visibleMessages));
    setMode(payload.mode);
    setBlockedStep(payload.blockedStep);
    setAvailableActions(payload.availableActions);
    setHitlRequestCas(payload.hitlRequestCas);
    setComposerState(
      payload.composerState ?? getInitialComposerState(payload.executionStatus)
    );
  }, []);

  const requestExecutionChat = useCallback(
    async (
      body: Parameters<typeof invokeExecutionChat>[0]
    ): Promise<ExecutionChatResponse | null> => {
      try {
        return await invokeExecutionChat(body);
      } catch (invokeError) {
        setError(
          invokeError instanceof Error
            ? invokeError.message
            : 'Erreur du chat d’exécution.'
        );
        return null;
      }
    },
    []
  );

  const refresh = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!executionId) {
        setMessages([]);
        setBlockedStep(null);
        setAvailableActions([]);
        setHitlRequestCas(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      const silent = options?.silent ?? false;
      if (!silent) setIsLoading(true);
      setError(null);

      const payload = await requestExecutionChat({
        action: 'resume',
        executionId,
      });
      if (payload) {
        applyPayload(payload);
      }

      if (!silent) setIsLoading(false);
    },
    [applyPayload, executionId, requestExecutionChat]
  );

  const refreshFromLastKnownCreatedAt = useCallback(async () => {
    if (!executionId) return;
    const payload = await requestExecutionChat({
      action: 'resume',
      executionId,
      ...(lastKnownCreatedAtRef.current
        ? { sinceCreatedAt: lastKnownCreatedAtRef.current }
        : {}),
    });
    if (payload) {
      applyPayload(payload);
    }
  }, [applyPayload, executionId, requestExecutionChat]);

  const streamPersistedAssistantMessage = useCallback(
    async (row: ExecutionChatMessageRow) => {
      const text = getStreamablePersistedMessageText(row);
      if (!text || activeExecutionIdRef.current !== row.execution_id) {
        streamingPersistedMessageIdsRef.current.delete(row.id);
        setMessages((previous) => mergeMessages(previous, [row]));
        return;
      }

      streamingPersistedMessageIdsRef.current.add(row.id);
      let accumulated = '';
      setStreamingStatus(null);

      try {
        for (const chunk of chunkText(text)) {
          if (activeExecutionIdRef.current !== row.execution_id) return;
          accumulated += chunk;
          setStreamingText(accumulated);
          await sleep(REALTIME_MESSAGE_CHUNK_DELAY_MS);
        }

        if (activeExecutionIdRef.current !== row.execution_id) return;
        setStreamingText(null);
        setMessages((previous) => mergeMessages(previous, [row]));
        await refreshFromLastKnownCreatedAt();
      } finally {
        streamingPersistedMessageIdsRef.current.delete(row.id);
      }
    },
    [refreshFromLastKnownCreatedAt]
  );

  const enqueuePersistedAssistantMessageStream = useCallback(
    (row: ExecutionChatMessageRow) => {
      streamingPersistedMessageIdsRef.current.add(row.id);
      const nextStream = persistedMessageStreamQueueRef.current.then(() =>
        streamPersistedAssistantMessage(row)
      );
      persistedMessageStreamQueueRef.current = nextStream.catch(() => {});
    },
    [streamPersistedAssistantMessage]
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    lastKnownCreatedAtRef.current = lastMessage?.created_at ?? null;
  }, [messages]);

  useEffect(() => {
    if (!executionId) return;

    let isActive = true;
    const channel = supabase
      .channel(`execution-chat-${executionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'execution_chat_messages',
          filter: `execution_id=eq.${executionId}`,
        },
        (payload) => {
          if (!isActive) return;
          if (payload.eventType === 'DELETE') {
            const deleted = payload.old as { id?: unknown } | null;
            if (typeof deleted?.id !== 'string') return;
            setMessages((previous) =>
              previous.filter((message) => message.id !== deleted.id)
            );
            return;
          }

          const row = payload.new as ExecutionChatMessageRow | null;
          if (!row || typeof row.id !== 'string') return;

          if (
            payload.eventType === 'INSERT' &&
            shouldStreamPersistedMessage(
              row,
              executionStatusRef.current,
              isSubmittingRef.current
            )
          ) {
            enqueuePersistedAssistantMessageStream(row);
            return;
          }

          setMessages((previous) => mergeMessages(previous, [row]));
          if (
            payload.eventType === 'INSERT' &&
            (row.actor === 'system' || row.actor === 'assistant')
          ) {
            void refreshFromLastKnownCreatedAt();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'execution_steps',
          filter: `execution_id=eq.${executionId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') return;
          const nextRow = (payload.new ?? null) as Record<string, unknown> | null;
          const previousRow = (payload.old ?? null) as Record<string, unknown> | null;
          const nextStatus = typeof nextRow?.status === 'string' ? nextRow.status : null;
          const previousStatus =
            typeof previousRow?.status === 'string' ? previousRow.status : null;
          if (
            (nextStatus === 'waiting_hitl' || nextStatus === 'failed') &&
            nextStatus !== previousStatus
          ) {
            void refreshFromLastKnownCreatedAt();
          }
        }
      )
      .subscribe((status) => {
        if (!isActive) return;
        if (status === 'SUBSCRIBED') {
          void refreshFromLastKnownCreatedAt();
        }
      });

    return () => {
      isActive = false;
      void supabase.removeChannel(channel);
    };
  }, [
    enqueuePersistedAssistantMessageStream,
    executionId,
    refreshFromLastKnownCreatedAt,
  ]);

  // Polling fallback while a send/act is in flight. Realtime + SSE deliver the
  // reply in the happy path, but if either is buffered/degraded (e.g. SSE held
  // open by a proxy) the persisted assistant message would otherwise only show
  // after a manual reload. Dedup by id makes this harmless when streaming works.
  useEffect(() => {
    if (!executionId || !isSubmitting) return;
    const intervalId = window.setInterval(() => {
      void refreshFromLastKnownCreatedAt();
    }, 2000);
    return () => window.clearInterval(intervalId);
  }, [executionId, isSubmitting, refreshFromLastKnownCreatedAt]);

  const runAct = useCallback(
    async (
      actionId: string,
      args?: Record<string, unknown>,
      options?: { clientMessage?: string }
    ): Promise<boolean> => {
      if (!executionId) return false;
      setIsSubmitting(true);
      setError(null);
      const payload = await requestExecutionChat(attachExecutionChatHitlCas({
        action: 'act',
        executionId,
        actionId,
        ...(args ? { args } : {}),
        ...(options?.clientMessage ? { clientMessage: options.clientMessage } : {}),
      }, hitlRequestCas));
      if (payload) {
        applyPayload(payload);
        setIsSubmitting(false);
        return 'handled' in payload ? payload.handled !== false : true;
      }
      setIsSubmitting(false);
      return false;
    },
    [applyPayload, executionId, hitlRequestCas, requestExecutionChat]
  );

  const sendMessage = useCallback(
    async (message: string): Promise<boolean> => {
      const text = message.trim();
      if (!executionId || text.length === 0) return false;
      setIsSubmitting(true);
      setError(null);
      setStreamingText(null);
      setStreamingStatus(null);

      try {
        let accumulated = '';
        const payload = await streamExecutionChat(
          attachExecutionChatHitlCas({
            executionId,
            message: text,
          }, hitlRequestCas),
          {
            onChunk: (chunk) => {
              setStreamingStatus(null);
              accumulated += chunk;
              setStreamingText(accumulated);
            },
            onStatus: (status) => {
              if (accumulated.length === 0) {
                setStreamingStatus(status);
              }
            },
          }
        );
        applyPayload(payload);
        setStreamingText(null);
        setStreamingStatus(null);
        setIsSubmitting(false);
        return 'handled' in payload ? payload.handled !== false : true;
      } catch (streamErr) {
        setStreamingText(null);
        setStreamingStatus(null);
        // If the stream was already established the message is persisted
        // server-side, so refresh instead of re-sending (a re-send would
        // duplicate the reply). Otherwise the request never landed — re-send.
        const established =
          streamErr instanceof ExecutionChatStreamError && streamErr.established;
        const payload = await requestExecutionChat(
          established
            ? { action: 'resume', executionId }
            : attachExecutionChatHitlCas({
                action: 'send',
                executionId,
                message: text,
              }, hitlRequestCas)
        );
        if (payload) {
          applyPayload(payload);
          setIsSubmitting(false);
          return 'handled' in payload ? payload.handled !== false : true;
        }
        setError(streamErr instanceof Error ? streamErr.message : 'Erreur de streaming.');
        setIsSubmitting(false);
        return false;
      }
    },
    [applyPayload, executionId, hitlRequestCas, requestExecutionChat]
  );

  return {
    messages,
    mode,
    blockedStep,
    availableActions,
    composerState,
    hitlRequestCas,
    isLoading,
    isSubmitting,
    streamingText,
    streamingStatus,
    error,
    refresh,
    runAct,
    sendMessage,
  };
};
